/**
 * client/render/modelcache.ts — GLB cache for entity models (bodies, anim packs, weapons, missiles).
 * OWNER: RENDER agent.
 */
import type { Object3D, Skeleton, SkinnedMesh } from 'three';
import { GLTFLoader, type GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { sharedTexturesPlugin } from '../assetload/sharedTextures';

export class ModelCache {
  // one Texture per shared image file across GLBs (assets agent, CONTRACT-REQUESTS #A1)
  private readonly loader = new GLTFLoader().register(sharedTexturesPlugin());
  private readonly map = new Map<string, Promise<GLTF>>();
  load(url: string): Promise<GLTF> {
    let p = this.map.get(url);
    if (!p) { p = this.loader.loadAsync(url); this.map.set(url, p); }
    return p;
  }
}

/**
 * Dispose the per-instance GPU state of a SkeletonUtils.clone()d model: every clone gets its own Skeleton,
 * and three.js gives each Skeleton a bone texture (setProgram -> boneTexture) that is only freed by
 * skeleton.dispose(). Geometry and source textures are shared with the cached GLTF and are NOT disposed.
 * Returns how many skeletons were disposed. (Fix for the 1-texture-per-projectile leak, verify tex-leak.mjs.)
 */
export function disposeSkeletons(root: Object3D): number {
  const seen = new Set<Skeleton>();
  root.traverse((o) => {
    const sm = o as SkinnedMesh;
    if (sm.isSkinnedMesh && sm.skeleton) seen.add(sm.skeleton);
  });
  for (const s of seen) s.dispose();
  return seen.size;
}

/**
 * Give every SkinnedMesh under `root` a bind-pose bounding sphere up front. three.js otherwise computes
 * SkinnedMesh.boundingSphere lazily on the first render (projectObject depth sort) by skinning EVERY vertex
 * on the CPU (applyBoneTransform): ~55 ms per player clone, 440 ms when 8 players appear in the first match
 * frame (verify perf-spikes 533 ms frame, CPU profile). The sphere is only used for sorting/culling (our
 * entity meshes are not frustum culled), so the bind pose is close enough. Returns the number set.
 */
export function presetSkinnedBounds(root: Object3D): number {
  let n = 0;
  root.traverse((o) => {
    const sm = o as SkinnedMesh;
    if (!sm.isSkinnedMesh || sm.boundingSphere) return;
    if (!sm.geometry.boundingSphere) sm.geometry.computeBoundingSphere();
    sm.boundingSphere = sm.geometry.boundingSphere!.clone();
    n++;
  });
  return n;
}
